import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react';

const LIMIAR_ARRASTE_PX = 4;
const FAIXA_VISIVEL_PX = 48;

const SELETOR_SEM_ARRASTE =
  'button, a, input, textarea, select, label, [contenteditable="true"], [data-no-drag]';

function alvoBloqueiaArraste(alvo: EventTarget | null, alca: HTMLElement): boolean {
  if (!(alvo instanceof Element)) return true;
  const bloqueado = alvo.closest(SELETOR_SEM_ARRASTE);
  return bloqueado != null && alca.contains(bloqueado);
}

function limitar(
  propostoX: number,
  propostoY: number,
  naturalLeft: number,
  naturalTop: number,
  dialogWidth: number,
  alcaHeight: number,
): { x: number; y: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const minX = FAIXA_VISIVEL_PX - dialogWidth - naturalLeft;
  const maxX = vw - FAIXA_VISIVEL_PX - naturalLeft;
  const minY = FAIXA_VISIVEL_PX - alcaHeight - naturalTop;
  const maxY = vh - FAIXA_VISIVEL_PX - naturalTop;
  return {
    x: Math.min(maxX, Math.max(minX, propostoX)),
    y: Math.min(maxY, Math.max(minY, propostoY)),
  };
}

type SessaoArraste = {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startOffsetX: number;
  startOffsetY: number;
  naturalLeft: number;
  naturalTop: number;
  dialogWidth: number;
  alcaHeight: number;
  moveu: boolean;
};

export type UseArrastarDialogArgs = {
  open: boolean;
  dialogRef: RefObject<HTMLElement | null>;
  alcaRef: RefObject<HTMLElement | null>;
};

export type UseArrastarDialogResult = {
  deslocamento: { x: number; y: number };
  arrastando: boolean;
  onAlcaPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  /** Envolve o onClose do overlay: ignora o click que segue um arraste. */
  onOverlayClick: (fechar: () => void) => void;
};

/**
 * Arraste de dialog pelo header/toolbar. Nao inicia em controles clicaveis.
 * Deslocamento via transform; ao fechar, zera.
 */
export function useArrastarDialog({
  open,
  dialogRef,
  alcaRef,
}: UseArrastarDialogArgs): UseArrastarDialogResult {
  const [deslocamento, setDeslocamento] = useState({ x: 0, y: 0 });
  const [arrastando, setArrastando] = useState(false);
  const deslocamentoRef = useRef(deslocamento);
  deslocamentoRef.current = deslocamento;
  const ignorarClickOverlayRef = useRef(false);
  const sessaoRef = useRef<SessaoArraste | null>(null);

  const limparListeners = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!open) {
      limparListeners.current?.();
      limparListeners.current = null;
      setDeslocamento({ x: 0, y: 0 });
      setArrastando(false);
      sessaoRef.current = null;
      ignorarClickOverlayRef.current = false;
    }
  }, [open]);

  useEffect(
    () => () => {
      limparListeners.current?.();
      limparListeners.current = null;
    },
    [],
  );

  const onAlcaPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (event.button !== 0) return;
      const dialog = dialogRef.current;
      const alca = alcaRef.current;
      if (!dialog || !alca) return;
      if (alvoBloqueiaArraste(event.target, alca)) return;

      event.preventDefault();
      const rect = dialog.getBoundingClientRect();
      const atual = deslocamentoRef.current;
      sessaoRef.current = {
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startOffsetX: atual.x,
        startOffsetY: atual.y,
        naturalLeft: rect.left - atual.x,
        naturalTop: rect.top - atual.y,
        dialogWidth: rect.width,
        alcaHeight: alca.offsetHeight || FAIXA_VISIVEL_PX,
        moveu: false,
      };
      setArrastando(true);
      alca.setPointerCapture(event.pointerId);

      limparListeners.current?.();

      const onMove = (moveEvent: PointerEvent) => {
        const sessao = sessaoRef.current;
        if (!sessao || moveEvent.pointerId !== sessao.pointerId) return;
        const dx = moveEvent.clientX - sessao.startClientX;
        const dy = moveEvent.clientY - sessao.startClientY;
        if (!sessao.moveu && Math.hypot(dx, dy) >= LIMIAR_ARRASTE_PX) {
          sessao.moveu = true;
        }
        const limitado = limitar(
          sessao.startOffsetX + dx,
          sessao.startOffsetY + dy,
          sessao.naturalLeft,
          sessao.naturalTop,
          sessao.dialogWidth,
          sessao.alcaHeight,
        );
        setDeslocamento(limitado);
      };

      const encerrar = (upEvent: PointerEvent) => {
        const sessao = sessaoRef.current;
        if (!sessao || upEvent.pointerId !== sessao.pointerId) return;
        if (sessao.moveu) {
          ignorarClickOverlayRef.current = true;
        }
        sessaoRef.current = null;
        setArrastando(false);
        limparListeners.current?.();
        limparListeners.current = null;
        const alcaAtual = alcaRef.current;
        if (alcaAtual?.hasPointerCapture(upEvent.pointerId)) {
          alcaAtual.releasePointerCapture(upEvent.pointerId);
        }
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', encerrar);
      window.addEventListener('pointercancel', encerrar);
      limparListeners.current = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', encerrar);
        window.removeEventListener('pointercancel', encerrar);
      };
    },
    [alcaRef, dialogRef],
  );

  const onOverlayClick = useCallback((fechar: () => void) => {
    if (ignorarClickOverlayRef.current) {
      ignorarClickOverlayRef.current = false;
      return;
    }
    fechar();
  }, []);

  return { deslocamento, arrastando, onAlcaPointerDown, onOverlayClick };
}
